/**
 * API client — wraps fetch to Worker, attaches Worker JWT.
 * Worker JWT is stored in-memory (refresh on page reload via LIFF idToken).
 */
import { useAuthStore } from './store';
import { getIDToken, login as liffLogin, isInLineClient } from './liff';
import type { ApiResponse } from '@payroll/shared';
import type { AuthProfile } from '@payroll/shared';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8787';

class ApiError extends Error {
  constructor(public code: string, message: string, public status: number, public details?: unknown) {
    super(message);
  }
}

// ป้ายชื่อไทยของฟิลด์ที่ validate บ่อย — ใช้แปลง path ของ zod issue ให้คนอ่านออก
// (ครอบคลุมฟอร์มพนักงานเป็นหลัก; ฟิลด์ที่ไม่มีในแมป fallback เป็นชื่อ path ตรงๆ)
const FIELD_LABELS_TH: Record<string, string> = {
  emp_code: 'รหัสพนักงาน', first_name: 'ชื่อ', last_name: 'นามสกุล', nickname: 'ชื่อเล่น',
  email: 'อีเมล', phone: 'เบอร์โทร', department: 'แผนก', position: 'ตำแหน่ง',
  supervisor_email: 'อีเมลหัวหน้า', start_date: 'วันเริ่มงาน', end_date: 'วันสิ้นสุด',
  status: 'สถานะ', schedule_mode: 'รูปแบบวันหยุด', work_mode: 'รูปแบบทำงาน',
  national_id: 'เลขบัตรประชาชน', birth_date: 'วันเกิด', sso_number: 'เลขประกันสังคม',
  bank: 'ธนาคาร', bank_account_masked: 'เลขบัญชี', bank_account_full: 'เลขบัญชี',
  address_current: 'ที่อยู่ปัจจุบัน', address_registered: 'ที่อยู่ตามทะเบียนบ้าน',
  emergency_name: 'ชื่อผู้ติดต่อฉุกเฉิน', emergency_relationship: 'ความสัมพันธ์',
  emergency_phone: 'เบอร์ฉุกเฉิน', rotation_anchor_date: 'วันเริ่มรอบเวร',
};

/** แปลง zod issues (ฟิลด์ `details` ของ error VALIDATION_ERROR) เป็นข้อความไทยที่บอกว่า
 *  ฟิลด์ไหนผิด — ไม่งั้นผู้ใช้เห็นแค่ "validation failed" ลอยๆ หาสาเหตุไม่เจอ. */
function formatValidationDetails(details: unknown): string | null {
  if (!Array.isArray(details) || details.length === 0) return null;
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const d of details) {
    if (!d || typeof d !== 'object') continue;
    const path = Array.isArray((d as { path?: unknown }).path)
      ? ((d as { path: unknown[] }).path).join('.')
      : '';
    const msg = typeof (d as { message?: unknown }).message === 'string'
      ? (d as { message: string }).message
      : '';
    const label = FIELD_LABELS_TH[path] ?? path;
    const line = label ? `${label}: ${msg || 'ข้อมูลไม่ถูกต้อง'}` : msg;
    if (line && !seen.has(line)) { seen.add(line); parts.push(line); }
  }
  return parts.length ? `ข้อมูลไม่ผ่านการตรวจสอบ — ${parts.join(' · ')}` : null;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
  skipAuth?: boolean;
  /** Explicit bearer token override — used by cross-app handoff
   *  (`#token=…`) before the store has been populated. */
  token?: string;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return apiFetchInner<T>(path, opts, true);
}

async function apiFetchInner<T>(path: string, opts: RequestOptions, allowReauth: boolean): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (opts.token) {
    headers.Authorization = `Bearer ${opts.token}`;
  } else if (!opts.skipAuth) {
    const jwt = useAuthStore.getState().jwt;
    if (jwt) headers.Authorization = `Bearer ${jwt}`;
  }

  if (opts.idempotencyKey) headers['X-Idempotency-Key'] = opts.idempotencyKey;

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  const json = (await res.json()) as ApiResponse<T>;

  if (!json.ok) {
    if (!opts.skipAuth && allowReauth && isAuthFailure(json.code, res.status)) {
      const recovered = await refreshWorkerSession();
      if (recovered) return apiFetchInner<T>(path, opts, false);
    }
    const friendly =
      json.code === 'VALIDATION_ERROR' ? formatValidationDetails(json.details) : null;
    throw new ApiError(json.code, friendly ?? json.error, res.status, json.details);
  }
  return json.data;
}

function isAuthFailure(code: string, status: number): boolean {
  return status === 401 && (code === 'JWT_EXPIRED' || code === 'UNAUTHORIZED' || code === 'AUTH_FAILED');
}

/** Download a binary response (e.g. a PDF) with the Worker JWT attached, then
 *  trigger a browser save. Used for endpoints that stream bytes (not a signed URL). */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const jwt = useAuthStore.getState().jwt;
  const res = await fetch(`${API_BASE}${path}`, {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
  });
  if (!res.ok) throw new ApiError('DOWNLOAD_FAILED', 'ดาวน์โหลดไม่สำเร็จ', res.status);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function refreshWorkerSession(): Promise<boolean> {
  useAuthStore.getState().clear();
  const idToken = getIDToken();
  if (!idToken) {
    // Only auto-bounce to LINE OAuth inside the LINE in-app client. In a
    // browser, web-login (password) users have no LINE identity and LINE-login
    // users chose LINE explicitly — either way we route back to /login (which
    // leads with email+password) rather than forcing a LINE redirect.
    if (isInLineClient()) liffLogin();
    return false;
  }
  try {
    const result = await apiFetchInner<{ jwt: string; profile: AuthProfile }>(
      '/auth/me',
      { method: 'POST', body: { idToken }, skipAuth: true },
      false,
    );
    useAuthStore.getState().setAuth(result.jwt, result.profile);
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.code === 'AUTH_FAILED' && isInLineClient()) liffLogin();
    return false;
  }
}

/**
 * Upload file via Worker proxy → R2 binding.
 *
 * The Worker reads the multipart body and writes via env.FILES.put, so
 * we don't need R2 access keys plumbed through wrangler secrets.
 * `file` should already be watermarked client-side (see lib/watermark.ts)
 * before reaching here.
 */
export async function uploadFile(
  file: Blob,
  purpose: 'selfie' | 'evidence' | 'avatar' | 'identity' | 'logo' | 'temp' | 'signature' | 'contract',
  filename = 'upload.jpg',
): Promise<string> {
  const jwt = useAuthStore.getState().jwt;
  const form = new FormData();
  // Provide a filename so the server-side File has a usable .name
  form.append('file', file, filename);
  form.append('purpose', purpose);

  // 30s upload timeout — without this an iOS Safari hang on a flaky
  // network would leave the spinner spinning forever and the user blamed
  // "ระบบนิ่ง". AbortController surfaces a real error we can catch + show.
  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => ctrl.abort(), 30_000);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
      body: form,
      signal: ctrl.signal,
    });
  } catch (e) {
    clearTimeout(timeoutId);
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new ApiError('UPLOAD_TIMEOUT', 'อัปโหลดไฟล์ค้างนานเกิน 30 วินาที กรุณาเช็คเน็ตและลองใหม่', 0);
    }
    throw e;
  }
  clearTimeout(timeoutId);
  const json = (await res.json()) as ApiResponse<{ path: string; size: number }>;
  if (!json.ok) throw new ApiError(json.code, json.error, res.status, json.details);
  return json.data.path;
}

/**
 * Upload a pairing proof (selfie / ID card) BEFORE the Worker JWT exists.
 * Authenticated by the LINE idToken + tenant_code instead of a Bearer token,
 * matching the /auth/pair flow. Returns the stored R2 path.
 */
export async function uploadPairFile(
  file: Blob,
  args: { idToken: string; tenantCode: string },
  filename = 'pair.jpg',
): Promise<string> {
  const form = new FormData();
  form.append('file', file, filename);
  form.append('idToken', args.idToken);
  form.append('tenant_code', args.tenantCode);

  const res = await fetch(`${API_BASE}/auth/pair-upload`, { method: 'POST', body: form });
  const json = (await res.json()) as ApiResponse<{ path: string }>;
  if (!json.ok) throw new ApiError(json.code, json.error, res.status, json.details);
  return json.data.path;
}

/**
 * Resolve a Worker-signed file URL (from /checkin/status etc.) into the
 * absolute URL the browser can put in `<img src>`. The Worker returns a
 * relative path like `/file?path=...&exp=...&sig=...`.
 */
export function resolveFileUrl(signed: string | null | undefined): string | null {
  if (!signed) return null;
  if (signed.startsWith('http')) return signed;
  return `${API_BASE}${signed}`;
}

export { ApiError };
